import { API } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ShipmentOps } from "./ShipmentOps";
import { OrderItemsCard } from "./OrderItemsCard";
import { canCreateShipment } from "./orderLineUtils";

export function StageSlaCard({ stages }) {
  const rows = stages || [];
  if (!rows.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="stage-sla-card">
      <h2 className="text-xl font-bold uppercase tracking-tight">Stage SLA — Expected vs Actual</h2>
      <table className="w-full text-sm mt-2">
        <thead><tr className="border-b border-border text-left">
          {["Stage", "Expected (min)", "Actual (min)", "Status"].map((h) => (
            <th key={h} className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>))}
        </tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.stage} className="border-b border-border/50" data-testid={`stage-sla-${s.stage}`}>
              <td className="px-3 py-2 capitalize">{s.stage.replace(/_/g, " ")}</td>
              <td className="px-3 py-2 metric">{s.expected_min ?? "—"}</td>
              <td className="px-3 py-2 metric">{s.actual_min != null ? (s.actual_min < 1 ? "<1" : s.actual_min) : "—"}{s.in_progress ? " (running)" : ""}</td>
              <td className="px-3 py-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${
                  s.status === "over" ? "bg-signal-red/15 text-signal-red" :
                  s.status === "within" ? "bg-signal-green/15 text-signal-green" : "bg-muted text-muted-foreground"}`}>
                  {s.status === "over" ? "over standard" : s.status === "within" ? "within standard" : "pending"}
                </span></td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export function OrderV2Sections({ order, fs, can, DSP_ROLES, DOCS_ROLES, actV2, openShipment, onChanged }) {
  const shipmentAllowed = canCreateShipment(order, fs);
  return (
    <>
      <OrderItemsCard items={order.items} />

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded-lg p-4" data-testid="order-totals-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Commercial Summary</p>
          {order.totals && (
            <div className="space-y-1 text-sm metric">
              <div className="flex justify-between"><span className="text-muted-foreground font-sans">Gross</span><span>₹{order.totals.gross?.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground font-sans">Discount</span><span>-₹{order.totals.discount?.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground font-sans">Taxable</span><span>₹{order.totals.taxable?.toLocaleString("en-IN")}</span></div>
              {order.totals.igst > 0 && <div className="flex justify-between"><span className="text-muted-foreground font-sans">IGST</span><span>₹{order.totals.igst?.toLocaleString("en-IN")}</span></div>}
              {order.totals.cgst > 0 && <div className="flex justify-between"><span className="text-muted-foreground font-sans">CGST + SGST</span><span>₹{(order.totals.cgst + order.totals.sgst)?.toLocaleString("en-IN")}</span></div>}
              {order.totals.charges_total > 0 && <div className="flex justify-between"><span className="text-muted-foreground font-sans">Other charges</span><span>₹{(order.totals.charges_total + (order.totals.charges_tax || 0))?.toLocaleString("en-IN")}</span></div>}
              <div className="flex justify-between border-t border-border pt-1.5 font-extrabold text-base"><span className="font-sans">Invoice Total</span><span className="text-primary">₹{order.totals.total?.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground font-sans">Received</span><span className="text-signal-green">₹{order.totals.received?.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground font-sans">Balance</span><span className="text-signal-amber">₹{order.totals.balance?.toLocaleString("en-IN")}</span></div>
            </div>
          )}
        </div>
        <div className="bg-card border border-border rounded-lg p-4" data-testid="order-invoices-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Invoices</p>
            {can(DOCS_ROLES) && order.sales_status === "approved" && (
              <Button size="sm" variant="outline" onClick={() => actV2("invoices")} data-testid="generate-invoice-button">Generate Invoice</Button>
            )}
          </div>
          {(order.invoices || []).length === 0 && <p className="text-sm text-muted-foreground">No invoices yet</p>}
          {(order.invoices || []).map((inv) => (
            <div key={inv.invoice_no} className="flex items-center justify-between border border-border rounded-md px-3 py-2 mb-1.5">
              <span className="metric font-bold text-primary text-sm">{inv.invoice_no}</span>
              <span className="metric text-sm">₹{inv.totals?.total?.toLocaleString("en-IN")}</span>
              <Button size="sm" variant="outline" onClick={() => window.open(`${API}/invoices/${inv.invoice_no}/pdf`, "_blank")} data-testid={`invoice-pdf-${inv.invoice_no}`}>PDF</Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4" data-testid="order-shipments-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Shipments ({(order.shipments || []).length})</p>
          {shipmentAllowed && can(DSP_ROLES) && (
            <Button size="sm" className="bg-signal-orange hover:bg-signal-orange/90 text-white" onClick={openShipment} data-testid="new-shipment-button">Create Shipment from Order</Button>
          )}
          {(order.shipments || []).length > 0 && (
            <Button size="sm" variant="outline" onClick={() => window.open(`${API}/orders/${order.bill_no}/dispatch-pack`, "_blank")} data-testid="order-dispatch-pack-button">Dispatch Pack (All)</Button>)}
        </div>
        {(order.shipments || []).length === 0 && <p className="text-sm text-muted-foreground">No shipments yet — partial/split dispatch supported.</p>}
        <div className="space-y-2">
          {(order.shipments || []).map((s) => (
            <div key={s.shipment_id} className="border border-border rounded-md p-3 space-y-2" data-testid={`shipment-${s.shipment_id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="metric font-bold text-primary">{s.shipment_id}</span>
                <StatusBadge status={s.status} />
                <span className="metric text-xs">{s.vehicle_number} · {s.driver_name}</span>
                {s.lr_number && <span className="metric text-xs text-muted-foreground">LR: {s.lr_number}</span>}
                {s.invoice_no && <span className="metric text-xs text-muted-foreground">Inv: {s.invoice_no}</span>}
              </div>
              <p className="text-xs metric text-muted-foreground">
                {s.lines.map((l) => `${l.sku}: ${l.quantity.toLocaleString()}`).join(" · ")}
                {s.loading_minutes != null && ` · loading ${s.loading_minutes}m`}
              </p>
              <ShipmentOps
                shipment={s}
                canDispatch={can(DSP_ROLES)}
                canSales={can(["admin", "warehouse_manager", "sales", "sales_manager", "sales_coordinator", "field_sales", "inside_sales", "overseas_sales", "sales_head"])}
                onChanged={onChanged}
              />
            </div>
          ))}
        </div>
      </div>

      {(order.versions || []).length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="order-versions-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Version History (current: v{order.version || 1})</p>
          {order.versions.map((v) => (
            <div key={v.version} className="border border-border rounded-md px-3 py-2 mb-1.5 text-sm">
              <span className="font-semibold">Version {v.version}</span>
              <span className="text-muted-foreground"> — {v.snapshot?.items?.map((i) => `${i.sku}: ${i.quantity.toLocaleString()}`).join(", ")}</span>
              <span className="block text-xs text-muted-foreground">changed by {v.changed_by} · {fmtDT(v.changed_at)} · reason: {v.reason}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
